import { CategoryLabel } from 'components/CategoryLabel/CategoryLabel'
import CourseDetailOutcome from 'components/CourseDetails/CourseDetailOutcome'
import CourseDetailsCard, {
  CourseIntakeOption,
} from 'components/CourseDetails/CourseDetailsCard'
import CourseDetailsOutline from 'components/CourseDetails/CourseDetailsOutline'
import EditorJsBlocks from 'components/EditorJsBlocks'
import dayjs from 'dayjs'
import { date } from 'lib/date'
import { useEffect, useRef, useState } from 'react'
import { Course, StartType } from 'types/course'

type CourseDetailsProps = {
  course: Course
}

export default function CourseDetails({ course }: CourseDetailsProps) {
  const [isOriginalCardVisible, setIsOriginalCardVisible] = useState(true)
  const originalCardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsOriginalCardVisible(entry.isIntersecting)
      },
      {
        threshold: 0,
      },
    )

    if (originalCardRef.current) {
      observer.observe(originalCardRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  const courseIntakeOptions = course.publicIntakes
    .slice()
    .sort((intakeA, intakeB) => {
      if (!intakeA.dateStart || !intakeB.dateStart) {
        return 0
      }
      if (!intakeA.dateStart) {
        return 1
      }
      if (!intakeB.dateStart) {
        return -1
      }
      if (intakeA.dateStart < intakeB.dateStart) {
        return -1
      }
      if (intakeA.dateStart > intakeB.dateStart) {
        return 1
      }
      return 0
    })
    .flatMap<CourseIntakeOption>((intake) => {
      const rolling = course.startType === StartType.ROLLING

      if (dayjs(intake.courseEdition.dateStart).isValid() || rolling) {
        return {
          value: intake.slug,
          label: rolling
            ? 'Start any time'
            : `${date(intake.courseEdition.dateStart).format('Do MMMM YYYY')}`,
          intake,
        }
      }
      return []
    })

  const [selectedOption, setSelectedOption] = useState<CourseIntakeOption>(
    courseIntakeOptions?.[0],
  )

  return (
    <>
      <div
        className='lg:grid gap-10 flex flex-col mb-64 lg:mb-0'
        style={{
          gridTemplateColumns: '1fr auto',
        }}
      >
        <div className='flex flex-col gap-4 lg:gap-8'>
          {course.courseProvider?.imageUrl && (
            <div>
              <img
                src={course.courseProvider.imageUrl}
                alt={course.courseProvider.title}
                className='max-w-sm max-h-32'
              />
            </div>
          )}
          <h1 className='text-purple-heart font-heading font-medium text-4xl lg:text-4xl leading-tight'>
            {course.title}
          </h1>
          {course.categories.length > 0 && (
            <div className='flex flex-wrap gap-2'>
              {course.categories.map((category) => (
                <CategoryLabel key={category.id} category={category} />
              ))}
            </div>
          )}
          <p className='font-semibold'>{course.overview}</p>
        </div>

        <div ref={originalCardRef} className='lg:row-span-2 w-full lg:w-80'>
          <div className='lg:sticky lg:top-36'>
            <CourseDetailsCard
              course={course}
              options={courseIntakeOptions}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
            />
          </div>
        </div>

        <div className='flex flex-col gap-8'>
          <div>
            <EditorJsBlocks data={course.pageDetailsJson} />
          </div>
          {course.learningOutcomes.length > 0 && (
            <div className='flex flex-col gap-6'>
              <h2 className='font-heading text-2xl font-medium text-tuna'>
                Learning Outcomes
              </h2>
              <div className='grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 2xl:grid-cols-4'>
                {course.learningOutcomes.map((outcome) => (
                  <CourseDetailOutcome
                    key={outcome.id}
                    learningOutcome={outcome}
                  />
                ))}
              </div>
            </div>
          )}
          {selectedOption && (
            <div className='flex flex-col gap-6'>
              <h2 className='font-heading text-2xl font-medium text-tuna'>
                Course outline
              </h2>
              <CourseDetailsOutline
                courseSegments={
                  selectedOption.intake.courseEdition.courseSegments
                }
              />
            </div>
          )}
        </div>
      </div>

      <CourseDetailsCard
        course={course}
        options={courseIntakeOptions}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        variant='sticky'
        isVisible={!isOriginalCardVisible}
      />
    </>
  )
}
